// see https://www.iso.org/iso-4217-currency-codes.html

//currencies with an exponent of 0 - 0 decimal digits
const exponentZeroCurrencyCodes = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'ISK',
  'JPY',
  'KMF',
  'KRW',
  'PYG',
  'RWF',
  'UGX',
  'UYI',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]

// currencies with an exponent of 3 - 3 decimal digits
const exponentThreeCurrencyCodes = ['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']

//currencies with an expont of 4 - 4 decimal digits
const exponentFourCurrencyCodes = ['CLF', 'UYW']

const sanitise = (rawAmount: string) => {
  /*
     - Remove all characters that are not digits, . or , (£1,000.00 GBP > 1,000.00)
     - Remove all but the last . or , (1,000.00 > 1000.00)
   */
  let santisedAmount = rawAmount.replace(/[^\d.,]/g, '')
  santisedAmount = santisedAmount.replace(/[.,](?=.*[.,])/g, '')
  return santisedAmount
}

const getExponent = (currencyCode: string) => {
  if (exponentZeroCurrencyCodes.includes(currencyCode)) return 0
  if (exponentThreeCurrencyCodes.includes(currencyCode)) return 3
  if (exponentFourCurrencyCodes.includes(currencyCode)) return 4
  return 2
}

const sanitiseMainUnits = (rawMainUnits: string) => {
  // Strip any remaining thousand separators after validation
  return rawMainUnits.replace(/\D/g, '')
}

const validate = (amount: string, currencyExponent: number): [boolean, string, string] => {
  /* 
    Returns if it is a valid amount, the main units and sub units
    Rules:
    - Split on . or , (should always result in only 1 or 2 parts)
    - If only 1 part after split, and part is not an empty string, then it is a whole value (e.g. 100 > ['100'])
    - If 2 parts but the 1st is an empty string then replace with 0 (e.g. .50 > 0.50)
    Currenncy with an exponent of 0:
    - If 2 parts then the 2nd part has to be 3 digits (e.g. 1,000 > ['1', '000'])
    Currency with an exponent of 2:
    - If 2 parts and the 2nd part has 2 digits then it is a decimal value (e.g. 1.00 > ['1', '00'])
    - If 2 parts and the 3rd part has 3 digits then it is a whole value (e.g. 1,000 > ['1', '000'])
    Currency with an exponent of 3:
    - Get the separator, all currencies with an exponent of 3 uses . as the decimal separator, and is the only way to tell a decimal value from a whole thousand value
    - If 2 parts and the 2nd part has 3 digits and the separator is a . then it is a decimal value (e.g. 1.000 > ['1', '000])
    - if 2 parts and the 2nd part has 3 digits and the separator is a , then is is a whole value (e.g. 1,000 > ['1', '000])
    Currency with an exponent of 4:
    - If 2 parts and the 2nd part has 4 digits then it is a decimal value (e.g. 1.0000 > ['1', '0000'])
    - if 2 parts and the 2nd part has 3 digits then it is a whole value (e.g. 1,000 > ['1', '000'])
   */
  let amountParts = amount.split(/\.|\,/)

  if (amountParts.length == 1) {
    // cover empty strings
    if (amountParts[0].length > 0) return [true, amount, '0']
    return [false, null, null]
  }

  if (!amountParts[0]) {
    amountParts[0] = '0'
  }

  if (currencyExponent == 0) {
    if (amountParts[1].length != 3) return [false, null, null]
    return [true, amount, '0']
  }

  if (currencyExponent == 2) {
    if (amountParts[1].length == 2) return [true, amountParts[0], amountParts[1]]
    if (amountParts[1].length == 3) return [true, amount, '0']
    return [false, null, null]
  }

  if (currencyExponent == 3) {
    let decimalSeparator = amount.charAt(amount.length - (currencyExponent + 1))
    if (amountParts[1].length == 3 && decimalSeparator == '.') return [true, amountParts[0], amountParts[1]]
    if (amountParts[1].length == 3 && decimalSeparator == ',') return [true, amount, '0']
    return [false, null, null]
  }

  if (currencyExponent == 4) {
    if (amountParts[1].length == 4) return [true, amountParts[0], amountParts[1]]
    if (amountParts[1].length == 3) return [true, amount, '0']
    return [false, null, null]
  }

  return [false, null, null]
}

const parseCurrency = (rawAmount: string, currency: string): [number, string] => {
  // returns the normalised lowest sub unit amount and the display ammount e.g. 1050 & £10.50
  const amount = sanitise(rawAmount)
  const currencyExponent = getExponent(currency)

  let [validAmount, mainUnits, subUnits]: any = validate(amount, currencyExponent)

  if (!validAmount) {
    return [null, null]
  }
  mainUnits = sanitiseMainUnits(mainUnits)
  const decimalAmount = `${mainUnits}.${subUnits}`
  const displayAmount = parseFloat(decimalAmount).toLocaleString(undefined, { style: 'currency', currency: currency })
  const lowestUnitAmount = parseInt(mainUnits) * Math.pow(10, currencyExponent) + parseInt(subUnits)

  if (lowestUnitAmount == 0) return [null, null]
  return [lowestUnitAmount, displayAmount]
}

export default parseCurrency
